import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * AppTooltip
 * -----------------------------------------------------------------------------
 * Production-grade tooltip primitive for the EPPREM application.
 *
 * Features:
 *  - Portal rendering into document.body (prevents clipping inside cards/modals).
 *  - Fixed positioning computed from the trigger's bounding rect.
 *  - Shared hover delay group (150ms show / 100ms hide) to prevent flicker
 *    when the pointer moves across adjacent triggers (icon rows, table actions).
 *  - Keyboard accessibility via focus/blur events and role="tooltip".
 *  - Mobile long-press support (500ms press-and-hold to reveal).
 *  - Auto-flip placement when the preferred side would overflow the viewport.
 *  - Pluggable analytics hook fired the first time the tooltip becomes visible
 *    per trigger instance.
 *
 * Governance:
 *  - Typography stays within the 10px–16px scale (tooltip text = 11px).
 *  - No inline color styling; uses semantic theme classes.
 *  - Replaces native `title` attributes across the app.
 * -----------------------------------------------------------------------------
 */

// -------------------- Shared Delay Manager --------------------

let __tooltipDelayTimer: ReturnType<typeof setTimeout> | null = null;
let __tooltipLastHideAt = 0;

const DEFAULT_SHOW_DELAY = 150;
const DEFAULT_HIDE_DELAY = 100;
/**
 * Window during which a newly-entered tooltip skips its show delay because the
 * user is clearly moving between adjacent triggers (icon rows, toolbars).
 */
const GROUP_HAND_OFF_WINDOW = 300;

function clearTooltipTimer() {
  if (__tooltipDelayTimer) {
    clearTimeout(__tooltipDelayTimer);
    __tooltipDelayTimer = null;
  }
}

export function showTooltipWithDelay(fn: () => void, delay: number = DEFAULT_SHOW_DELAY) {
  clearTooltipTimer();
  const skipDelay = Date.now() - __tooltipLastHideAt < GROUP_HAND_OFF_WINDOW;
  if (skipDelay) {
    fn();
    return;
  }
  __tooltipDelayTimer = setTimeout(fn, delay);
}

export function hideTooltipWithDelay(fn: () => void, delay: number = DEFAULT_HIDE_DELAY) {
  clearTooltipTimer();
  __tooltipDelayTimer = setTimeout(() => {
    __tooltipLastHideAt = Date.now();
    fn();
  }, delay);
}

// -------------------- Analytics --------------------

export type TooltipAnalyticsHandler = (payload: {
  content: string;
  page: string;
  timestamp: string;
}) => void;

let __tooltipAnalyticsHandler: TooltipAnalyticsHandler | null = null;

/**
 * Register an application-wide analytics handler for tooltip impressions.
 * Call once from an app bootstrap module (e.g. wire to backend analytics).
 */
export function registerTooltipAnalytics(handler: TooltipAnalyticsHandler | null) {
  __tooltipAnalyticsHandler = handler;
}

function trackTooltip(content: React.ReactNode) {
  if (!content) return;
  const text = typeof content === 'string' ? content : '[rich-tooltip]';
  const payload = {
    content: text,
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: new Date().toISOString(),
  };
  try {
    __tooltipAnalyticsHandler?.(payload);
  } catch {
    // Analytics must never break UX.
  }
}

// -------------------- Placement Math --------------------

type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

interface Coords {
  top: number;
  left: number;
  side: TooltipSide;
}

const ARROW_OFFSET = 8;
const VIEWPORT_PADDING = 8;

function computePlacement(
  triggerRect: DOMRect,
  tooltipRect: { width: number; height: number },
  preferredSide: TooltipSide,
): Coords {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const fits = (side: TooltipSide) => {
    switch (side) {
      case 'top':
        return triggerRect.top - tooltipRect.height - ARROW_OFFSET >= VIEWPORT_PADDING;
      case 'bottom':
        return triggerRect.bottom + tooltipRect.height + ARROW_OFFSET <= vh - VIEWPORT_PADDING;
      case 'left':
        return triggerRect.left - tooltipRect.width - ARROW_OFFSET >= VIEWPORT_PADDING;
      case 'right':
        return triggerRect.right + tooltipRect.width + ARROW_OFFSET <= vw - VIEWPORT_PADDING;
    }
  };

  const flipMap: Record<TooltipSide, TooltipSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };

  const side = fits(preferredSide) ? preferredSide : fits(flipMap[preferredSide]) ? flipMap[preferredSide] : preferredSide;

  let top = 0;
  let left = 0;

  switch (side) {
    case 'top':
      top = triggerRect.top - ARROW_OFFSET;
      left = triggerRect.left + triggerRect.width / 2;
      break;
    case 'bottom':
      top = triggerRect.bottom + ARROW_OFFSET;
      left = triggerRect.left + triggerRect.width / 2;
      break;
    case 'left':
      top = triggerRect.top + triggerRect.height / 2;
      left = triggerRect.left - ARROW_OFFSET;
      break;
    case 'right':
      top = triggerRect.top + triggerRect.height / 2;
      left = triggerRect.right + ARROW_OFFSET;
      break;
  }

  // Clamp horizontally so the tooltip never overflows the viewport when centered.
  const halfW = tooltipRect.width / 2;
  if (side === 'top' || side === 'bottom') {
    left = Math.max(VIEWPORT_PADDING + halfW, Math.min(left, vw - VIEWPORT_PADDING - halfW));
  }
  const halfH = tooltipRect.height / 2;
  if (side === 'left' || side === 'right') {
    top = Math.max(VIEWPORT_PADDING + halfH, Math.min(top, vh - VIEWPORT_PADDING - halfH));
  }

  return { top, left, side };
}

function transformForSide(side: TooltipSide): string {
  switch (side) {
    case 'top':
      return 'translate(-50%, -100%)';
    case 'bottom':
      return 'translate(-50%, 0)';
    case 'left':
      return 'translate(-100%, -50%)';
    case 'right':
      return 'translate(0, -50%)';
  }
}

// -------------------- Component --------------------

export interface AppTooltipProps {
  /** Tooltip text or rich content. When empty/null the wrapper renders without tooltip behavior. */
  content: React.ReactNode;
  /** Preferred side. Auto-flips if there isn't room. Default: 'top'. */
  side?: TooltipSide;
  /** Override the default 150ms show delay. */
  openDelay?: number;
  /** Override the default 100ms hide delay. */
  closeDelay?: number;
  /** Disable the tooltip without unmounting the trigger. */
  disabled?: boolean;
  /** Extra classes applied to the floating tooltip surface. */
  className?: string;
  /** The trigger element. Must be a single child. */
  children: React.ReactElement;
}

let __tooltipIdCounter = 0;

export const AppTooltip: React.FC<AppTooltipProps> = ({
  content,
  side = 'top',
  openDelay,
  closeDelay,
  disabled = false,
  className,
  children,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [coords, setCoords] = React.useState<Coords>({ top: 0, left: 0, side });
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const pressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackedRef = React.useRef(false);
  const idRef = React.useRef<string>(`app-tooltip-${++__tooltipIdCounter}`);

  const hasContent = content !== null && content !== undefined && content !== '';
  const enabled = hasContent && !disabled;

  const show = React.useCallback(() => {
    if (!enabled) return;
    showTooltipWithDelay(() => setVisible(true), openDelay);
  }, [enabled, openDelay]);

  const hide = React.useCallback(() => {
    hideTooltipWithDelay(() => setVisible(false), closeDelay);
  }, [closeDelay]);

  const cancelLongPress = React.useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  }, []);

  // Position + analytics after render.
  React.useLayoutEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const next = computePlacement(
      triggerRect,
      { width: tooltipRect.width, height: tooltipRect.height },
      side,
    );
    setCoords(next);
  }, [visible, side, content]);

  React.useEffect(() => {
    if (visible && !trackedRef.current) {
      trackedRef.current = true;
      trackTooltip(content);
    }
    if (!visible) {
      trackedRef.current = false;
    }
  }, [visible, content]);

  // Hide on scroll / resize / escape so the tooltip never floats in a stale position.
  React.useEffect(() => {
    if (!visible) return;

    const handleDismiss = () => setVisible(false);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };

    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
      window.removeEventListener('keydown', handleKey);
    };
  }, [visible]);

  // Cleanup on unmount.
  React.useEffect(() => () => cancelLongPress(), [cancelLongPress]);

  if (!React.isValidElement(children)) {
    return children as unknown as React.ReactElement;
  }

  // Wire handlers + ref onto the single child without introducing extra DOM.
  const child = children as React.ReactElement<Record<string, unknown>>;
  const existing = child.props as Record<string, unknown>;

  const mergedProps: Record<string, unknown> = {
    ...existing,
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const originalRef = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof originalRef === 'function') {
        originalRef(node);
      } else if (originalRef && typeof originalRef === 'object') {
        (originalRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: (e: React.MouseEvent) => {
      (existing.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
      show();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      (existing.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
      hide();
    },
    onFocus: (e: React.FocusEvent) => {
      (existing.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
      if (enabled) setVisible(true);
    },
    onBlur: (e: React.FocusEvent) => {
      (existing.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
      setVisible(false);
    },
    onTouchStart: (e: React.TouchEvent) => {
      (existing.onTouchStart as ((e: React.TouchEvent) => void) | undefined)?.(e);
      if (!enabled) return;
      cancelLongPress();
      pressTimerRef.current = setTimeout(() => setVisible(true), 500);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      (existing.onTouchEnd as ((e: React.TouchEvent) => void) | undefined)?.(e);
      cancelLongPress();
      setVisible(false);
    },
    onTouchCancel: (e: React.TouchEvent) => {
      (existing.onTouchCancel as ((e: React.TouchEvent) => void) | undefined)?.(e);
      cancelLongPress();
      setVisible(false);
    },
    'aria-describedby': visible
      ? idRef.current
      : (existing['aria-describedby'] as string | undefined),
  };

  const clonedTrigger = React.cloneElement(child, mergedProps);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  return (
    <>
      {clonedTrigger}
      {visible && enabled && portalTarget
        ? createPortal(
            <div
              ref={tooltipRef}
              id={idRef.current}
              role="tooltip"
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                transform: transformForSide(coords.side),
                pointerEvents: 'none',
              }}
              className={cn(
                'z-[9999] max-w-xs rounded-md bg-foreground px-2 py-1 text-[11px] font-medium leading-snug text-background shadow-lg',
                'animate-in fade-in-0 zoom-in-95',
                className,
              )}
            >
              {content}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
};

AppTooltip.displayName = 'AppTooltip';

export default AppTooltip;
